import { supabase } from '../lib/supabase';
import { formatEventLocationDisplay } from '../utils/eventLocation';
import {
  handleQuery,
  accessDeniedResponse,
  resolveRoleContext,
  applyFilters,
  applyOrdering,
  applyRange,
  applySearch,
  callRpc,
} from './utils';

const EMAIL_TEMPLATES_TABLE = 'email_templates';
const EMAIL_WORKFLOWS_TABLE = 'email_workflows';
const EMAIL_SENDS_TABLE = 'email_sends';
const EMAIL_QUEUE_TABLE = 'email_queue';
const EMAIL_LOGS_TABLE = 'email_logs';

/**
 * Ensure caller is admin. Marketing dashboard is admin-only.
 * @param {{ role?: string, userId?: string }} [options]
 */
const requireAdmin = async (options = {}) => {
  const roleContext = await resolveRoleContext(options);
  if (roleContext.error) return { error: roleContext.error };
  if (roleContext.role !== 'admin') {
    return { error: accessDeniedResponse('Marketing access is limited to administrators.').error };
  }
  return { role: roleContext.role, userId: roleContext.userId, error: null };
};

// --- Email Templates ---

/**
 * List all email templates for marketing dashboard.
 * @param {{
 *   role?: string,
 *   userId?: string,
 *   filters?: Array<{ column: string, operator: string, value: any }>,
 *   search?: string,
 *   orderBy?: { column: string, ascending?: boolean },
 *   range?: { from: number, to: number }
 * }} [options]
 */
export const listTemplates = async (options = {}) => {
  const ctx = await requireAdmin(options);
  if (ctx.error) return { data: null, error: ctx.error };

  const { filters, search, orderBy = { column: 'created_at', ascending: false }, range } = options;
  let query = supabase
    .from(EMAIL_TEMPLATES_TABLE)
    .select('*', { count: 'exact' });

  query = applyFilters(query, filters);
  query = applySearch(query, search, ['name', 'category', 'trigger_event']);
  query = applyOrdering(query, orderBy);
  query = applyRange(query, range);

  return handleQuery(query, { fallbackMessage: 'Unable to load email templates.' });
};

/**
 * Get a single email template by id.
 */
export const getTemplateById = async (templateId, options = {}) => {
  const ctx = await requireAdmin(options);
  if (ctx.error) return { data: null, error: ctx.error };

  const query = supabase
    .from(EMAIL_TEMPLATES_TABLE)
    .select('*')
    .eq('id', templateId)
    .maybeSingle();

  return handleQuery(query, {
    fallbackMessage: 'Unable to load the template.',
    allowEmpty: false,
  });
};

/**
 * Create an email template.
 * @param {Record<string, any>} payload
 */
export const createTemplate = async (payload, options = {}) => {
  const ctx = await requireAdmin(options);
  if (ctx.error) return { data: null, error: ctx.error };

  const data = {
    name: payload.name ?? 'Untitled',
    description: payload.description ?? null,
    category: payload.category ?? null,
    trigger_event: payload.trigger_event ?? 'custom',
    subject: payload.subject ?? '',
    html_body: payload.html_body ?? '',
    preview_text: payload.preview_text ?? null,
    variables_schema: payload.variables_schema ?? null,
    json_definition: payload.json_definition ?? null,
    is_active: payload.is_active ?? true,
    is_system: payload.is_system ?? false,
    agent_id: payload.agent_id ?? null,
    version: payload.version ?? 1,
    created_by: ctx.userId ?? null,
    language: payload.language ?? 'es',
  };

  const query = supabase.from(EMAIL_TEMPLATES_TABLE).insert(data).select('*').single();
  return handleQuery(query, {
    fallbackMessage: 'Unable to create the template.',
    allowEmpty: false,
  });
};

/**
 * Update an email template.
 */
export const updateTemplate = async (templateId, updates, options = {}) => {
  const ctx = await requireAdmin(options);
  if (ctx.error) return { data: null, error: ctx.error };

  const allowed = [
    'name', 'description', 'category', 'trigger_event', 'subject', 'html_body',
    'preview_text', 'variables_schema', 'json_definition', 'is_active', 'version', 'updated_at', 'language',
  ];
  const payload = {};
  for (const key of allowed) {
    if (updates.hasOwnProperty(key)) payload[key] = updates[key];
  }
  payload.updated_at = new Date().toISOString();

  const query = supabase
    .from(EMAIL_TEMPLATES_TABLE)
    .update(payload)
    .eq('id', templateId)
    .select('*')
    .single();

  return handleQuery(query, {
    fallbackMessage: 'Unable to update the template.',
    allowEmpty: false,
  });
};

/**
 * Delete an email template. System templates (is_system = true) should not be deleted from UI.
 */
export const deleteTemplate = async (templateId, options = {}) => {
  const ctx = await requireAdmin(options);
  if (ctx.error) return { data: null, error: ctx.error };

  const query = supabase
    .from(EMAIL_TEMPLATES_TABLE)
    .delete()
    .eq('id', templateId)
    .select('*')
    .single();

  return handleQuery(query, {
    fallbackMessage: 'Unable to delete the template.',
    allowEmpty: false,
  });
};

/**
 * Duplicate an email template. Creates a copy with name "Copy of {original name}",
 * all fields copied, is_system = false, is_active = true.
 * @param {string} templateId
 * @returns {Promise<{ data: object | null, error: object | null }>}
 */
export const duplicateTemplate = async (templateId, options = {}) => {
  const { data: original, error: fetchError } = await getTemplateById(templateId, options);
  if (fetchError || !original) {
    return { data: null, error: fetchError || { message: 'Template not found.', code: 'not_found' } };
  }
  const { id, created_at, updated_at, ...rest } = original;
  const payload = {
    ...rest,
    name: `Copy of ${(original.name || '').trim() || 'Untitled'}`,
    is_system: false,
    is_active: true,
  };
  return createTemplate(payload, options);
};

// --- Email Workflows ---

/**
 * List all email workflows.
 */
export const listWorkflows = async (options = {}) => {
  const ctx = await requireAdmin(options);
  if (ctx.error) return { data: null, error: ctx.error };

  const { filters, orderBy = { column: 'created_at', ascending: false }, range } = options;
  let query = supabase
    .from(EMAIL_WORKFLOWS_TABLE)
    .select(`
      *,
      email_templates:template_id ( id, name )
    `, { count: 'exact' });

  query = applyFilters(query, filters);
  query = applyOrdering(query, orderBy);
  query = applyRange(query, range);

  return handleQuery(query, { fallbackMessage: 'Unable to load workflows.' });
};

/**
 * Get a single workflow by id.
 */
export const getWorkflowById = async (workflowId, options = {}) => {
  const ctx = await requireAdmin(options);
  if (ctx.error) return { data: null, error: ctx.error };

  const query = supabase
    .from(EMAIL_WORKFLOWS_TABLE)
    .select(`
      *,
      email_templates:template_id ( id, name, subject )
    `)
    .eq('id', workflowId)
    .maybeSingle();

  return handleQuery(query, {
    fallbackMessage: 'Unable to load the workflow.',
    allowEmpty: false,
  });
};

/**
 * Create an email workflow.
 */
export const createWorkflow = async (payload, options = {}) => {
  const ctx = await requireAdmin(options);
  if (ctx.error) return { data: null, error: ctx.error };

  const data = {
    name: payload.name ?? 'Untitled Workflow',
    description: payload.description ?? null,
    trigger_event: payload.trigger_event,
    template_id: payload.template_id ?? null,
    conditions: payload.conditions ?? null,
    actions: payload.actions ?? null,
    delay_minutes: payload.delay_minutes ?? 0,
    is_active: payload.is_active ?? true,
    created_by: ctx.userId,
  };

  const query = supabase.from(EMAIL_WORKFLOWS_TABLE).insert(data).select('*').single();
  return handleQuery(query, {
    fallbackMessage: 'Unable to create the workflow.',
    allowEmpty: false,
  });
};

/**
 * Update an email workflow.
 */
export const updateWorkflow = async (workflowId, updates, options = {}) => {
  const ctx = await requireAdmin(options);
  if (ctx.error) return { data: null, error: ctx.error };

  const allowed = [
    'name', 'description', 'trigger_event', 'template_id', 'conditions', 'actions',
    'delay_minutes', 'is_active',
  ];
  const payload = {};
  for (const key of allowed) {
    if (updates.hasOwnProperty(key)) payload[key] = updates[key];
  }
  payload.updated_at = new Date().toISOString();

  const query = supabase
    .from(EMAIL_WORKFLOWS_TABLE)
    .update(payload)
    .eq('id', workflowId)
    .select('*')
    .single();

  return handleQuery(query, {
    fallbackMessage: 'Unable to update the workflow.',
    allowEmpty: false,
  });
};

/**
 * Delete an email workflow.
 */
export const deleteWorkflow = async (workflowId, options = {}) => {
  const ctx = await requireAdmin(options);
  if (ctx.error) return { data: null, error: ctx.error };

  const query = supabase
    .from(EMAIL_WORKFLOWS_TABLE)
    .delete()
    .eq('id', workflowId)
    .select('*')
    .single();

  return handleQuery(query, {
    fallbackMessage: 'Unable to delete the workflow.',
    allowEmpty: false,
  });
};

/**
 * Duplicate an email workflow. Creates a copy with name "Copy of {original name}",
 * all fields copied (trigger_event, template_id, description, conditions, actions, delay_minutes),
 * is_active = false.
 * @param {string} workflowId
 * @returns {Promise<{ data: object | null, error: object | null }>}
 */
export const duplicateWorkflow = async (workflowId, options = {}) => {
  const { data: original, error: fetchError } = await getWorkflowById(workflowId, options);
  if (fetchError || !original) {
    return { data: null, error: fetchError || { message: 'Workflow not found.', code: 'not_found' } };
  }
  const { id, created_at, updated_at, created_by, email_templates, ...rest } = original;
  const payload = {
    ...rest,
    name: `Copy of ${(original.name || '').trim() || 'Untitled Workflow'}`,
    template_id: original.template_id ?? null,
    conditions: original.conditions ?? null,
    actions: original.actions ?? null,
    is_active: false,
  };
  return createWorkflow(payload, options);
};

/**
 * Preview the events that match a set of workflow conditions. Used by the
 * "Preview affected events" panel in the workflow form so admins can sanity-check
 * conditions before saving.
 *
 * Conditions whose keys map to event-table fields are applied as filters; other
 * keys (e.g. reminder_interval, custom keys) are ignored for the preview because
 * they are evaluated per-trigger, not per-event.
 *
 * Restricted to public events. Events are returned regardless of session
 * timing — the modal labels each row as "Upcoming" or "Past" so admins can
 * see all events that could match the workflow conditions.
 *
 * @param {Record<string, any>} conditions
 * @param {{ role?: string, userId?: string, limit?: number, upcomingOnly?: boolean }} [options]
 * @returns {Promise<{ data: Array<object> | null, error: object | null, ignoredKeys: string[] }>}
 */
export const previewAffectedEvents = async (conditions = {}, options = {}) => {
  const ctx = await requireAdmin(options);
  if (ctx.error) return { data: null, error: ctx.error, ignoredKeys: [] };

  const limit = Math.max(1, Math.min(options.limit ?? 50, 200));
  const conditionEntries = Object.entries(conditions || {}).filter(
    ([, v]) => v !== null && v !== undefined && String(v).trim() !== ''
  );

  const supportedKeys = new Set([
    'event_id',
    'event_title',
    'event_type',
    'event_location_type',
    'event_language',
    'event_price',
    'agent_id',
  ]);
  const ignoredKeys = conditionEntries
    .map(([k]) => k)
    .filter((k) => !supportedKeys.has(k));

  // Use the explicit FK hint for event_sessions because there are two FKs between
  // events and event_sessions (event_sessions.event_id and events.orientation_session_id),
  // which makes PostgREST embed ambiguous without a named relationship.
  let query = supabase
    .from('events')
    .select(
      `
      id, title, type, format, language, status, price, agent_id, timezone,
      agent:users!events_agent_id_fkey ( id, full_name ),
      event_sessions:event_sessions!event_sessions_event_id_fkey ( id, start_time, end_time, session_number )
    `
    )
    .eq('status', 'public');

  for (const [key, rawVal] of conditionEntries) {
    const val = String(rawVal).trim();
    if (!supportedKeys.has(key)) continue;
    switch (key) {
      case 'event_id':
        query = query.eq('id', val);
        break;
      case 'event_title':
        query = query.eq('title', val);
        break;
      case 'event_type':
        query = query.eq('type', val);
        break;
      case 'event_location_type':
        query = query.eq('format', val);
        break;
      case 'event_language':
        query = query.eq('language', val);
        break;
      case 'agent_id':
        query = query.eq('agent_id', val);
        break;
      case 'event_price': {
        if (val.toLowerCase() === 'free') {
          query = query.or('price.is.null,price.eq.0');
        } else {
          const num = Number(val);
          if (!Number.isFinite(num)) {
            query = query.eq('price', val);
          } else {
            query = query.eq('price', num);
          }
        }
        break;
      }
      default:
        break;
    }
  }

  const { data, error } = await handleQuery(
    query.limit(limit),
    { fallbackMessage: 'Unable to preview matching events.' }
  );
  if (error) return { data: null, error, ignoredKeys };

  const nowMs = Date.now();
  const rawRows = Array.isArray(data) ? data : [];
  const eventIds = rawRows.map((ev) => ev.id).filter(Boolean);

  let countsByEvent = {};
  if (eventIds.length > 0) {
    const { data: regs, error: regsError } = await supabase
      .from('event_registrations')
      .select('event_id')
      .in('event_id', eventIds);
    if (!regsError && Array.isArray(regs)) {
      countsByEvent = regs.reduce((m, r) => {
        if (!r?.event_id) return m;
        m[r.event_id] = (m[r.event_id] || 0) + 1;
        return m;
      }, {});
    }
  }

  const rows = rawRows.map((ev) => {
    const sessions = Array.isArray(ev.event_sessions) ? ev.event_sessions : [];
    const withTimes = sessions
      .map((s) => ({
        ...s,
        ts: s.start_time ? new Date(s.start_time).getTime() : null,
      }))
      .filter((s) => s.ts !== null && !Number.isNaN(s.ts));

    const upcoming = withTimes
      .filter((s) => s.ts >= nowMs)
      .sort((a, b) => a.ts - b.ts);
    const past = withTimes
      .filter((s) => s.ts < nowMs)
      .sort((a, b) => b.ts - a.ts);

    const nextSession = upcoming[0] ?? null;
    const lastSession = past[0] ?? null;
    const displaySession = nextSession ?? lastSession ?? null;
    const hasUpcoming = upcoming.length > 0;

    return {
      id: ev.id,
      title: ev.title,
      type: ev.type,
      format: ev.format,
      language: ev.language,
      status: ev.status,
      price: ev.price,
      agent_id: ev.agent_id,
      agent_name: ev.agent?.full_name ?? '',
      timezone: ev.timezone ?? null,
      next_session_start: nextSession?.start_time ?? null,
      last_session_start: lastSession?.start_time ?? null,
      display_session_start: displaySession?.start_time ?? null,
      registered_count: countsByEvent[ev.id] ?? 0,
      has_upcoming: hasUpcoming,
      session_count: withTimes.length,
    };
  });

  // Sort: upcoming events first (soonest first), then past events (most recent first),
  // then events with no scheduled sessions at the bottom.
  rows.sort((a, b) => {
    if (a.has_upcoming !== b.has_upcoming) return a.has_upcoming ? -1 : 1;
    if (a.has_upcoming && b.has_upcoming) {
      const at = new Date(a.next_session_start).getTime();
      const bt = new Date(b.next_session_start).getTime();
      return at - bt;
    }
    const at = a.display_session_start ? new Date(a.display_session_start).getTime() : -Infinity;
    const bt = b.display_session_start ? new Date(b.display_session_start).getTime() : -Infinity;
    return bt - at;
  });

  const finalRows = options.upcomingOnly ? rows.filter((r) => r.has_upcoming) : rows;

  return { data: finalRows, error: null, ignoredKeys };
};

// --- Email Sends (Quick Send tracking) ---

/**
 * Create an email_sends record and optionally queue emails.
 * @param {{
 *   subject: string,
 *   body: string,
 *   template_id?: string,
 *   segment_type: string,
 *   segment_filters?: object,
 *   recipient_list?: Array<{ email: string, name?: string }>,
 *   total_recipients: number
 * }} payload
 */
export const createEmailSend = async (payload, options = {}) => {
  const ctx = await requireAdmin(options);
  if (ctx.error) return { data: null, error: ctx.error };

  const data = {
    subject: payload.subject,
    body: payload.body,
    template_id: payload.template_id ?? null,
    segment_type: payload.segment_type,
    segment_filters: payload.segment_filters ?? null,
    recipient_list: payload.recipient_list ?? null,
    sent_by: ctx.userId,
    status: 'draft',
    total_recipients: payload.total_recipients ?? 0,
    sent_count: 0,
  };

  const query = supabase.from(EMAIL_SENDS_TABLE).insert(data).select('*').single();
  return handleQuery(query, {
    fallbackMessage: 'Unable to create the send record.',
    allowEmpty: false,
  });
};

/**
 * Update email_sends (e.g. status to 'sending' then 'sent').
 */
export const updateEmailSend = async (sendId, updates, options = {}) => {
  const ctx = await requireAdmin(options);
  if (ctx.error) return { data: null, error: ctx.error };

  const allowed = ['status', 'sent_count', 'total_recipients'];
  const payload = {};
  for (const key of allowed) {
    if (updates.hasOwnProperty(key)) payload[key] = updates[key];
  }
  payload.updated_at = new Date().toISOString();

  const query = supabase
    .from(EMAIL_SENDS_TABLE)
    .update(payload)
    .eq('id', sendId)
    .select('*')
    .single();

  return handleQuery(query, {
    fallbackMessage: 'Unable to update the send record.',
    allowEmpty: false,
  });
};

/**
 * List email_sends for dashboard.
 */
export const getEmailSends = async (options = {}) => {
  const ctx = await requireAdmin(options);
  if (ctx.error) return { data: null, error: ctx.error };

  const { orderBy = { column: 'created_at', ascending: false }, range } = options;
  let query = supabase
    .from(EMAIL_SENDS_TABLE)
    .select('*', { count: 'exact' });

  query = applyOrdering(query, orderBy);
  query = applyRange(query, range);

  return handleQuery(query, { fallbackMessage: 'Unable to load sends.' });
};

// --- Workflow context & merge tags (Quick Send) ---

/**
 * Build per-recipient workflow context for merge tag resolution.
 * @param {string} leadId
 * @param {string} eventId
 */
export const buildWorkflowContext = async (leadId, eventId, options = {}) => {
  const ctx = await requireAdmin(options);
  if (ctx.error) return { data: null, error: ctx.error };
  if (!leadId) {
    return { data: null, error: { message: 'Lead id is required for merge tags.', code: 'invalid_input' } };
  }
  return callRpc(
    'build_workflow_context',
    { p_lead_id: leadId, p_event_id: eventId ?? null },
    'Unable to build email context.',
  );
};

/**
 * Substitute merge tags in subject or HTML using workflow context.
 */
export const substituteEmailVariables = async (text, context, options = {}) => {
  const ctx = await requireAdmin(options);
  if (ctx.error) return { data: null, error: ctx.error };
  return callRpc(
    'substitute_email_variables',
    { p_text: text ?? '', p_context: context ?? {} },
    'Unable to render merge tags.',
  );
};

/**
 * Resolve preview context for Quick Send: first audience member, sample recipient, or placeholders.
 * @param {string} eventId
 * @param {{ email?: string, name?: string, lead_id?: string | null } | null} recipient
 * @param {{ role?: string, userId?: string }} [options]
 */
const resolveQuickSendPreviewContext = async (eventId, recipient, options = {}) => {
  const sampleRecipient = {
    email: 'maria@example.com',
    name: 'Maria Garcia',
  };

  const target = recipient && (recipient.email || '').trim() ? recipient : null;

  if (!target) {
    const context = await buildFallbackQuickSendContext(
      eventId,
      sampleRecipient.email,
      sampleRecipient.name,
    );
    return {
      context,
      preview_source: 'placeholders',
      preview_label: 'Sample recipient (Maria Garcia) — no audience loaded yet',
    };
  }

  let leadId = target.lead_id || null;
  if (!leadId) {
    const { data: row } = await supabase
      .from('leads')
      .select('id')
      .eq('event_id', eventId)
      .eq('email', (target.email || '').trim())
      .maybeSingle();
    leadId = row?.id ?? null;
  }

  const displayName = target.name || target.email;

  if (leadId) {
    const { data, error } = await buildWorkflowContext(leadId, eventId, options);
    if (error) return { error };
    if (data && typeof data === 'object') {
      return {
        context: data,
        preview_source: 'recipient',
        preview_label: `Preview for ${displayName} (first in audience, full merge context)`,
      };
    }
  }

  const context = await buildFallbackQuickSendContext(eventId, target.email, target.name);
  return {
    context,
    preview_source: 'recipient_partial',
    preview_label: `Preview for ${displayName} (first in audience, event-level tags)`,
  };
};

/**
 * Render subject/body for Quick Send preview (read-only; does not queue or send).
 * @param {{
 *   subject?: string,
 *   body?: string,
 *   event_id?: string | null,
 *   recipient?: { email?: string, name?: string, lead_id?: string | null } | null
 * }} params
 */
export const previewQuickSendEmail = async (params, options = {}) => {
  const ctx = await requireAdmin(options);
  if (ctx.error) return { data: null, error: ctx.error };

  const subject = (params.subject ?? '').trim();
  const body = (params.body ?? '').trim();
  const eventId = params.event_id ?? null;

  if (!subject && !body) {
    return {
      data: null,
      error: { message: 'Enter a subject or body to preview.', code: 'invalid_input' },
    };
  }

  if (!eventId) {
    return {
      data: {
        subject,
        body,
        preview_source: 'raw',
        preview_label: 'No event selected — merge tags shown as written',
      },
      error: null,
    };
  }

  const resolved = await resolveQuickSendPreviewContext(eventId, params.recipient ?? null, options);
  if (resolved.error) {
    return { data: null, error: resolved.error };
  }

  const { context, preview_source, preview_label } = resolved;

  const { data: renderedSubject, error: subjError } = await substituteEmailVariables(
    subject,
    context,
    options,
  );
  if (subjError) return { data: null, error: subjError };

  const { data: renderedBody, error: bodyError } = await substituteEmailVariables(
    body,
    context,
    options,
  );
  if (bodyError) return { data: null, error: bodyError };

  return {
    data: {
      subject: renderedSubject ?? subject,
      body: renderedBody ?? body,
      preview_source,
      preview_label,
    },
    error: null,
  };
};

const dedupeQuickSendRecipients = (list) => {
  const byEmail = new Map();
  for (const r of list) {
    const key = (r.email || '').trim().toLowerCase();
    if (!key) continue;
    const existing = byEmail.get(key);
    if (!existing) {
      byEmail.set(key, r);
      continue;
    }
    if (!existing.lead_id && r.lead_id) {
      byEmail.set(key, { ...existing, lead_id: r.lead_id, name: r.name || existing.name });
    }
  }
  return [...byEmail.values()];
};

const mapLeadRows = (rows) =>
  (rows || [])
    .filter((r) => (r.email || '').trim())
    .map((r) => ({
      email: r.email.trim(),
      name: r.full_name?.trim() || undefined,
      lead_id: r.id,
      audience: 'lead',
    }));

/** Resolve contact from event_registrations via lead, student user, or denormalized student_* columns. */
const resolveRegistrationContact = (row) => {
  const email = (
    row.lead?.email
    || row.student?.email
    || row.student_email
    || ''
  ).trim();
  const name = (
    row.lead?.full_name
    || row.student?.full_name
    || row.student_name
    || ''
  ).trim() || undefined;
  const leadId = row.lead_id || row.lead?.id || null;
  return { email, name, lead_id: leadId, audience: 'registered' };
};

const mapRegistrationRows = (rows) =>
  (rows || [])
    .map(resolveRegistrationContact)
    .filter((r) => r.email);

const enrichCustomRecipientsWithLeads = async (list, eventId) => {
  if (!eventId || !list.length) return list;
  const enriched = await Promise.all(
    list.map(async (r) => {
      if (r.lead_id) return r;
      const email = (r.email || '').trim();
      if (!email) return r;
      const { data: row } = await supabase
        .from('leads')
        .select('id')
        .eq('event_id', eventId)
        .eq('email', email)
        .maybeSingle();
      return row?.id ? { ...r, lead_id: row.id } : r;
    }),
  );
  return enriched;
};

/**
 * Minimal merge context when no lead exists (custom emails). Includes event-level tags when possible.
 */
const buildFallbackQuickSendContext = async (eventId, email, name) => {
  const trimmedEmail = (email || '').trim();
  const trimmedName = (name || '').trim() || 'there';

  const [{ data: event }, { data: settingsRows }] = await Promise.all([
    supabase
      .from('events')
      .select(`
        id, title, description, language, type, format, location_name, location_address, price,
        zoom_meeting_link, zoom_meeting_id, zoom_meeting_passcode, agent_id,
        agent:users!events_agent_id_fkey ( full_name, email, phone )
      `)
      .eq('id', eventId)
      .maybeSingle(),
    supabase.from('platform_settings').select('platform_name, app_base_url').limit(1),
  ]);

  const settings = Array.isArray(settingsRows) ? settingsRows[0] : settingsRows;
  const baseUrl = (settings?.app_base_url || 'https://prosperitymentors.net').replace(/\/$/, '');
  const agent = event?.agent;

  return {
    event_id: eventId,
    lead_id: '',
    recipient_email: trimmedEmail,
    recipient_name: trimmedName,
    student_email: trimmedEmail,
    student_name: trimmedName,
    event_title: event?.title || 'Event',
    event_description: event?.description || '',
    event_language: event?.language || 'es',
    event_type: event?.type || '',
    event_location_type: event?.format || '',
    event_location: formatEventLocationDisplay(event),
    event_location_name: event?.location_name ?? event?.location ?? '',
    event_location_address: event?.location_address ?? '',
    event_price: event?.price != null ? String(event.price) : '',
    agent_name: agent?.full_name || '',
    agent_email: agent?.email || '',
    agent_phone: agent?.phone || '',
    agent_id: event?.agent_id || '',
    zoom_link: event?.zoom_meeting_link || '',
    zoom_meeting_id: event?.zoom_meeting_id || '',
    zoom_passcode: event?.zoom_meeting_passcode || '',
    platform_name: settings?.platform_name || 'Prosperity Leaders',
    dashboard_link: `${baseUrl}/dashboard`,
    complete_registration_link: `${baseUrl}/complete-registration/${eventId}`,
  };
};

// --- Email Queue (for Quick Send) ---

/**
 * Queue one or more emails for sending. Inserts into email_queue.
 * Optionally updates email_sends record to 'sending' and increments sent_count when processed elsewhere.
 * @param {Array<{
 *   recipient_email: string,
 *   recipient_name?: string,
 *   subject: string,
 *   body: string,
 *   template_id?: string,
 *   variables?: object,
 *   event_id?: string,
 *   send_at?: string
 * }>} items
 * @param {{ email_send_id?: string, role?: string, userId?: string }} [options]
 */
export const queueEmailsForSend = async (items, options = {}) => {
  const ctx = await requireAdmin(options);
  if (ctx.error) return { data: null, error: ctx.error };

  const rows = items.map((item) => ({
    recipient_email: item.recipient_email,
    recipient_name: item.recipient_name ?? null,
    subject: item.subject,
    body: item.body ?? null,
    template_id: item.template_id ?? null,
    variables: item.variables ?? {},
    event_id: item.event_id ?? null,
    status: 'pending',
    send_at: item.send_at ?? null,
  }));

  const query = supabase.from(EMAIL_QUEUE_TABLE).insert(rows).select('id');
  return handleQuery(query, {
    fallbackMessage: 'Unable to queue emails.',
    allowEmpty: false,
  });
};

/**
 * Queue a Quick Send blast: resolve merge tags per recipient via build_workflow_context,
 * optionally at multiple scheduled send times (one email_queue row per recipient per time).
 * @param {{
 *   event_id: string,
 *   recipients: Array<{ email: string, name?: string, lead_id?: string | null }>,
 *   subject: string,
 *   body: string,
 *   template_id?: string | null,
 *   send_at_times?: Array<string | null>
 * }} params
 * @param {{ email_send_id?: string, role?: string, userId?: string }} [options]
 */
export const queueQuickSendBlast = async (params, options = {}) => {
  const ctx = await requireAdmin(options);
  if (ctx.error) return { data: null, error: ctx.error };

  const {
    event_id: eventId,
    recipients,
    subject,
    body,
    template_id: templateId = null,
    send_at_times: sendAtTimes,
  } = params;

  if (!eventId) {
    return { data: null, error: { message: 'Event is required.', code: 'invalid_input' } };
  }
  if (!subject?.trim() || !body?.trim()) {
    return { data: null, error: { message: 'Subject and body are required.', code: 'invalid_input' } };
  }

  const scheduleTimes =
    Array.isArray(sendAtTimes) && sendAtTimes.length > 0 ? sendAtTimes : [null];

  const queueItems = [];
  const skipped = [];

  const resolveLeadId = async (recipient) => {
    if (recipient.lead_id) return recipient.lead_id;
    const email = (recipient.email || '').trim();
    if (!email) return null;
    const { data: row } = await supabase
      .from('leads')
      .select('id')
      .eq('event_id', eventId)
      .eq('email', email)
      .maybeSingle();
    return row?.id ?? null;
  };

  for (const recipient of recipients || []) {
    if (!(recipient.email || '').trim()) {
      continue;
    }
    const leadId = await resolveLeadId(recipient);
    let vars;
    if (leadId) {
      const { data: context, error: contextError } = await buildWorkflowContext(
        leadId,
        eventId,
        options,
      );
      if (contextError) {
        return { data: null, error: contextError };
      }
      if (!context || typeof context !== 'object') {
        skipped.push(recipient.email);
        continue;
      }
      vars = context;
    } else {
      vars = await buildFallbackQuickSendContext(
        eventId,
        recipient.email,
        recipient.name,
      );
    }
    const recipientEmail =
      (vars.recipient_email || vars.student_email || recipient.email || '').trim();
    if (!recipientEmail) {
      skipped.push(recipient.email);
      continue;
    }
    const recipientName =
      vars.recipient_name || vars.student_name || recipient.name || null;

    const { data: renderedSubject, error: subjError } = await substituteEmailVariables(
      subject.trim(),
      vars,
      options,
    );
    if (subjError) {
      return { data: null, error: subjError };
    }
    const { data: renderedBody, error: bodyError } = await substituteEmailVariables(
      body.trim(),
      vars,
      options,
    );
    if (bodyError) {
      return { data: null, error: bodyError };
    }

    for (const sendAt of scheduleTimes) {
      queueItems.push({
        recipient_email: recipientEmail,
        recipient_name: recipientName,
        subject: renderedSubject ?? subject.trim(),
        body: renderedBody ?? body.trim(),
        template_id: templateId,
        variables: vars,
        event_id: eventId,
        send_at: sendAt ?? null,
      });
    }
  }

  if (queueItems.length === 0) {
    return {
      data: null,
      error: {
        message:
          skipped.length > 0
            ? 'Could not build email context for any recipient.'
            : 'No emails to queue.',
        code: 'no_recipients',
      },
    };
  }

  const { data, error } = await queueEmailsForSend(queueItems, options);
  if (error) {
    return { data: null, error };
  }

  const recipientCount = (recipients || []).filter((r) => (r.email || '').trim()).length - skipped.length;

  return {
    data: {
      queued: queueItems.length,
      recipients: recipientCount,
      skipped: skipped.length,
      queue_ids: data,
    },
    error: null,
  };
};

// --- Email Logs ---

/**
 * Get email logs (email_logs joined with email_queue for subject/event etc).
 * @param {{
 *   role?: string,
 *   userId?: string,
 *   filters?: Array<{ column: string, operator: string, value: any }>,
 *   search?: string,
 *   orderBy?: { column: string, ascending?: boolean },
 *   range?: { from: number, to: number }
 * }} [options]
 */
export const getEmailLogs = async (options = {}) => {
  const ctx = await requireAdmin(options);
  if (ctx.error) return { data: null, error: ctx.error };

  const { filters, search, orderBy = { column: 'sent_at', ascending: false }, range } = options;

  let query = supabase
    .from(EMAIL_LOGS_TABLE)
    .select(`
      id,
      email_queue_id,
      recipient_email,
      subject,
      status,
      sent_at,
      opened_at,
      clicked_at,
      created_at,
      email_queue:email_queue_id ( event_id, template_id )
    `, { count: 'exact' });

  query = applyFilters(query, filters);
  if (search) {
    query = query.or(`recipient_email.ilike.%${search}%,subject.ilike.%${search}%`);
  }
  query = applyOrdering(query, orderBy);
  query = applyRange(query, range);

  const { data, error, count } = await query;
  if (error) {
    return {
      data: null,
      error: { message: error.message || 'Unable to load email logs.', code: error.code },
      count: 0,
    };
  }
  return { data: data ?? [], error: null, count: count ?? 0 };
};

/**
 * Get a single email log entry with full details (e.g. for modal).
 */
export const getEmailLogById = async (logId, options = {}) => {
  const ctx = await requireAdmin(options);
  if (ctx.error) return { data: null, error: ctx.error };

  const query = supabase
    .from(EMAIL_LOGS_TABLE)
    .select(`
      *,
      email_queue:email_queue_id ( id, recipient_email, recipient_name, subject, body, template_id, event_id, variables, status, sent_at )
    `)
    .eq('id', logId)
    .maybeSingle();

  return handleQuery(query, {
    fallbackMessage: 'Unable to load the log entry.',
    allowEmpty: false,
  });
};

/**
 * Get recipient list for Quick Send audience. Used to preview and to build queue.
 * @param {{
 *   segment_type: 'registered' | 'leads' | 'unpaid_leads' | 'both' | 'custom' | 'all_leads' | 'paid' | 'individual',
 *   event_id?: string,
 *   recipient_list?: Array<{ email: string, name?: string }>
 * }} params
 * @param {{ role?: string, userId?: string }} [options]
 * @returns {Promise<{ data: Array<{ email: string, name?: string, lead_id?: string | null, audience?: string }> | null, error: object | null }>}
 */
export const getRecipientsForSegment = async (params, options = {}) => {
  const ctx = await requireAdmin(options);
  if (ctx.error) return { data: null, error: ctx.error };

  const { segment_type, event_id, recipient_list } = params;

  if (
    (segment_type === 'custom' || segment_type === 'individual')
    && Array.isArray(recipient_list)
  ) {
    const normalized = recipient_list
      .map((r) => ({
        email: (r.email || '').trim(),
        name: (r.name || '').trim() || undefined,
        lead_id: r.lead_id ?? null,
        audience: 'custom',
      }))
      .filter((r) => r.email);
    if (!event_id) {
      return { data: normalized, error: null };
    }
    const enriched = await enrichCustomRecipientsWithLeads(normalized, event_id);
    return { data: enriched, error: null };
  }

  if (!event_id) {
    return { data: [], error: null };
  }

  const fetchLeads = async (extraFilter) => {
    let q = supabase
      .from('leads')
      .select('id, email, full_name, payment_status')
      .eq('event_id', event_id)
      .not('email', 'is', null);
    if (extraFilter) q = extraFilter(q);
    const { data: rows, error } = await q;
    if (error) return { data: null, error: { message: error.message, code: error.code } };
    return { data: mapLeadRows(rows), error: null };
  };

  const fetchRegistrations = async (paidOnly = false) => {
    let q = supabase
      .from('event_registrations')
      .select(`
        lead_id,
        student_id,
        student_email,
        student_name,
        payment_status,
        lead:leads!lead_id ( id, email, full_name ),
        student:users!student_id ( id, email, full_name )
      `)
      .eq('event_id', event_id);
    if (paidOnly) {
      q = q.eq('payment_status', 'completed');
    }
    const { data: rows, error } = await q;
    if (error) return { data: null, error: { message: error.message, code: error.code } };
    return { data: mapRegistrationRows(rows), error: null };
  };

  if (segment_type === 'leads' || segment_type === 'all_leads') {
    return fetchLeads();
  }

  if (segment_type === 'registered' || segment_type === 'paid') {
    return fetchRegistrations(segment_type === 'paid');
  }

  if (segment_type === 'unpaid_leads') {
    const { data: leadRows, error: leadError } = await supabase
      .from('leads')
      .select('id, email, full_name, payment_status')
      .eq('event_id', event_id)
      .not('email', 'is', null);
    if (leadError) {
      return { data: null, error: { message: leadError.message, code: leadError.code } };
    }
    const { data: regRows, error: regError } = await supabase
      .from('event_registrations')
      .select('lead_id')
      .eq('event_id', event_id)
      .not('lead_id', 'is', null);
    if (regError) {
      return { data: null, error: { message: regError.message, code: regError.code } };
    }
    const registeredLeadIds = new Set((regRows || []).map((r) => r.lead_id));
    const unpaid = (leadRows || []).filter(
      (l) => (l.payment_status || '') !== 'paid' && !registeredLeadIds.has(l.id),
    );
    return { data: mapLeadRows(unpaid), error: null };
  }

  if (segment_type === 'both') {
    const [leadsResult, regResult] = await Promise.all([fetchLeads(), fetchRegistrations(false)]);
    if (leadsResult.error) return leadsResult;
    if (regResult.error) return regResult;
    return { data: dedupeQuickSendRecipients([...regResult.data, ...leadsResult.data]), error: null };
  }

  return { data: [], error: null };
};
